import React from 'react'

const TraceStats = ({ stats, loading, activeFilter, onFilterChange, onOpenIncidencePage }) => {
    const successRate = stats?.success_rate?.toFixed(1) ?? 0
    const rateColor = !stats ? 'text-gray-400' :
        stats.success_rate >= 95 ? 'text-green-600 dark:text-green-400' :
        stats.success_rate >= 80 ? 'text-yellow-600 dark:text-yellow-400' :
        'text-red-600 dark:text-red-400'

    const rateBarColor = !stats ? 'bg-gray-300' :
        stats.success_rate >= 95 ? 'bg-green-500' :
        stats.success_rate >= 80 ? 'bg-yellow-500' :
        'bg-red-500'

    return (
        <div className="w-44 flex-shrink-0 flex flex-col gap-2.5">
            {/* Stat cards */}
            {[
                {
                    label: 'Todos',
                    filter: 'all',
                    value: loading ? '—' : stats?.total_traces?.toLocaleString() ?? '0',
                    icon: 'analytics',
                    iconClass: 'text-blue-500',
                    bgClass: 'bg-blue-50 dark:bg-blue-950/30',
                    activeBorderClass: 'border-blue-400 dark:border-blue-500',
                },
                {
                    label: 'Sucesso',
                    filter: 'completed',
                    value: loading ? '—' : stats?.completed?.toLocaleString() ?? '0',
                    sub: !loading && stats ? `${stats.total_traces > 0 ? ((stats.completed / stats.total_traces) * 100).toFixed(0) : 0}%` : null,
                    icon: 'check_circle',
                    iconClass: 'text-green-500',
                    bgClass: 'bg-green-50 dark:bg-green-950/30',
                    activeBorderClass: 'border-green-400 dark:border-green-500',
                },
                {
                    label: 'Falhas',
                    filter: 'failed',
                    value: loading ? '—' : stats?.failed?.toLocaleString() ?? '0',
                    sub: !loading && stats ? `${stats.total_traces > 0 ? ((stats.failed / stats.total_traces) * 100).toFixed(0) : 0}%` : null,
                    icon: 'error',
                    iconClass: 'text-red-500',
                    bgClass: 'bg-red-50 dark:bg-red-950/30',
                    activeBorderClass: 'border-red-400 dark:border-red-500',
                },
                {
                    label: 'Falhas Reprocessadas',
                    filter: 'reprocessed_failed',
                    value: loading ? '—' : stats?.reprocessed_failed?.toLocaleString() ?? '0',
                    sub: !loading && stats ? `${stats.total_traces > 0 ? (((stats.reprocessed_failed || 0) / stats.total_traces) * 100).toFixed(0) : 0}%` : null,
                    icon: 'restart_alt',
                    iconClass: 'text-amber-500',
                    bgClass: 'bg-amber-50 dark:bg-amber-950/30',
                    activeBorderClass: 'border-amber-400 dark:border-amber-500',
                },
                {
                    label: 'Processando',
                    filter: 'running',
                    value: loading ? '—' : stats?.running?.toLocaleString() ?? '0',
                    icon: 'autorenew',
                    iconClass: 'text-blue-400 dark:text-blue-300',
                    bgClass: 'bg-blue-50 dark:bg-blue-950/30',
                    activeBorderClass: 'border-blue-400 dark:border-blue-500',
                },
            ].map((card) => {
                const isActive = activeFilter === card.filter
                // "Falhas" is the only card with a secondary action (open the
                // incidence-grouping screen), which needs a real nested
                // <button> - so this card is a <div role="button"> instead of
                // a <button> (a <button> can't legally contain another one).
                const showIncidenceButton = card.filter === 'failed' && onOpenIncidencePage && Number(stats?.failed) > 0
                return (
                    <div
                        key={card.label}
                        role="button"
                        tabIndex={0}
                        onClick={() => onFilterChange?.(card.filter)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onFilterChange?.(card.filter)
                            }
                        }}
                        className={`w-full text-left cursor-pointer ${card.bgClass} rounded-lg border-2 px-2.5 py-1.5 transition-all ${
                            isActive
                                ? `${card.activeBorderClass} shadow-sm`
                                : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                    >
                        <div className="flex items-center justify-between mb-1.5">
                            <p className="text-[11px] leading-tight text-gray-500 dark:text-gray-400 truncate">{card.label}</p>
                            <span className={`material-icons flex-shrink-0 ${card.iconClass}`} style={{ fontSize: '13px' }}>{card.icon}</span>
                        </div>
                        <div className="flex items-baseline justify-between">
                            <p className={`text-base font-bold leading-none ${card.iconClass}`}>{card.value}</p>
                            {card.sub && (
                                <p className="text-[10px] leading-none text-gray-400 dark:text-gray-500">{card.sub}</p>
                            )}
                        </div>

                        {showIncidenceButton && (
                            <div className="mt-1.5 pt-1.5 border-t border-red-200/70 dark:border-red-900/50">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onOpenIncidencePage() }}
                                    title="Ver incidências"
                                    className="w-full flex items-center justify-center gap-1 text-[10px] font-medium text-red-400/80 dark:text-red-400/70 hover:text-red-600 dark:hover:text-red-300 transition"
                                >
                                    <span className="material-icons leading-none" style={{ fontSize: '11px' }}>north_east</span>
                                    Incidências
                                </button>
                            </div>
                        )}
                    </div>
                )
            })}

            {/* Success rate */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-1.5">
                <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[11px] leading-tight text-gray-500 dark:text-gray-400">Taxa de Sucesso</p>
                    <span className={`material-icons flex-shrink-0 ${rateColor}`} style={{ fontSize: '13px' }}>trending_up</span>
                </div>
                <p className={`text-base font-bold leading-none ${rateColor}`}>{loading ? '—' : `${successRate}%`}</p>
                <div className="mt-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-[width] duration-700 ease-out ${rateBarColor}`}
                        style={{ width: loading ? '0%' : `${Math.min(Math.max(parseFloat(stats?.success_rate) || 0, 0), 100)}%` }}
                    />
                </div>
            </div>
        </div>
    )
}

export default TraceStats
